import { useState } from 'react';
import type { WaTemplate } from '../../../mocks/whatsapp';

interface Props {
  template: WaTemplate;
  onPreview: (template: WaTemplate) => void;
  onUse: (template: WaTemplate) => void;
}

export default function WhatsAppTemplateCard({ template, onPreview, onUse }: Props) {
  const [hovered, setHovered] = useState(false);

  const statusColors: Record<string, string> = {
    Active: 'bg-emerald-50 text-emerald-700',
    'Pending Approval': 'bg-amber-50 text-amber-700',
    Rejected: 'bg-red-50 text-red-700',
  };

  const categoryColors: Record<string, string> = {
    Booking: 'bg-emerald-50 text-emerald-600',
    Payment: 'bg-amber-50 text-amber-600',
    Reminder: 'bg-indigo-50 text-indigo-600',
    Visa: 'bg-violet-50 text-violet-600',
    Custom: 'bg-rose-50 text-rose-600',
  };

  const previewBody = template.bodyText.slice(0, 120).replace(/\*([^*]+)\*/g, '$1') + '...';

  return (
    <div
      className={`bg-white rounded-xl border transition-all duration-200 cursor-pointer overflow-hidden ${hovered ? 'border-gray-300' : 'border-gray-100'}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${template.color}18` }}
        >
          <i className={`${template.icon} text-lg`} style={{ color: template.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-800 leading-tight">{template.name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${statusColors[template.status]}`}>
              {template.status}
            </span>
          </div>
          <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${categoryColors[template.category]}`}>
            {template.category}
          </span>
        </div>
      </div>

      {/* Description */}
      <div className="px-4 pb-3">
        <p className="text-xs text-gray-500 leading-relaxed mb-3">{template.description}</p>

        {/* WhatsApp Preview Bubble */}
        <div className="bg-[#f0fdf4] rounded-xl p-3 border border-green-100 relative">
          <div className="absolute top-2 left-2 w-2 h-2 bg-[#f0fdf4] border-l border-b border-green-100 rotate-45 -translate-x-1" />
          {template.headerText && (
            <p className="text-xs font-semibold text-gray-800 mb-1">{template.headerText}</p>
          )}
          <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-line">{previewBody}</p>
          <p className="text-xs text-gray-400 text-right mt-1.5">9:14 AM ✓✓</p>
        </div>

        {/* Buttons preview */}
        {template.buttons && template.buttons.length > 0 && (
          <div className="mt-2 space-y-1">
            {template.buttons.map((btn, i) => (
              <div
                key={i}
                className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-green-200 bg-white text-xs font-medium text-[#25D366]"
              >
                <i className={`${btn.type === 'URL' ? 'ri-external-link-line' : btn.type === 'PHONE' ? 'ri-phone-line' : 'ri-chat-check-line'} text-sm`} />
                {btn.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="px-4 py-3 border-t border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <i className="ri-send-plane-line" />
            {template.usageCount} sent
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {template.lastUsed}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onPreview(template)}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Preview
          </button>
          {template.status === 'Active' && (
            <button
              onClick={() => onUse(template)}
              className="text-xs px-2.5 py-1.5 rounded-lg text-white font-medium transition-colors cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#25D366' }}
            >
              Use
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
